import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { Hex } from 'viem';
import { environment } from '../../environments/environment';
import {
  BattleLogEvent,
  BattleStartResponsePayload,
  ChainSubscriptionResponsePayload,
  ChatHistoryMessage,
  ChatMessageResponsePayload,
  CommitterRevealPayload,
  CommitterSlotRevealPayload,
  DualSignedRevealPayload,
  DualSignedSlotRevealPayload,
  SlotContribPayload,
  CoopTurnPayload,
  GameStatsPayload,
  DailyBoardPayload,
  LeaderboardResponsePayload,
  LinkTwitterBeginReplyPayload,
  LinkTwitterRejectedPayload,
  MyResponse,
  OnlineUser,
  ProfileResponsePayload,
  QueueJoinRejectedPayload,
  QueueListing,
  Request,
  ResponseType,
  SignedCommitPayload,
  StaleTurnPayload,
  BattlePresencePayload,
  SimplePMSharesBoughtPayload,
  SimplePMSharesClaimedPayload,
  BattleSettledPayload,
  SignedDirectChallengeReceivedPayload,
  SignedQueueMatchedPayload,
  SignedQueueMatchAssignedPayload,
  PartyCardPayload,
  SeatClaimResultPayload,
  PartyUpdatedPayload,
  PartyReadyPayload,
  FrensTicketPayload,
  QueueMatchAbortedPayload,
  QueueStatePayload,
  RequestType,
  StatsSummary,
  TwitterLinkedPayload,
  TwitterUnlinkedPayload,
} from '../types/server';
import { prefixHex } from '../utils/hex';
import { bigintReplacer, formatPlayerName } from './utils.service';

type PayloadOf<T> = MyResponse extends infer R ? (R extends { type: T; payload: infer P } ? P : never) : never;
type ForwardRoutes = { [T in MyResponse['type']]?: { next(payload: PayloadOf<T>): void } };

// Flattens the server-side identity bundle into OnlineUser. `wins`/`losses`
// stay optional for backward compat with templates that already guarded on
// them; `twitterHandle` is the new addition from the OAuth link flow.
function onlineUserFromPayload(
  username: string,
  stats: StatsSummary | null,
  twitterHandle: string | null
): OnlineUser {
  const base: OnlineUser = { username, twitterHandle };
  if (stats === null) {
    return base;
  }
  return { ...base, wins: stats.wins, losses: stats.losses };
}

// ===== WS history buffer (turn-flow / matchmaking only) =====

export interface WsLogEntry {
  ts: number;
  direction: 'IN' | 'OUT';
  type: string;
  summary?: Record<string, unknown>;
}

const WS_HISTORY_LIMIT = 20;

const INBOUND_WS_WHITELIST: ReadonlySet<ResponseType> = new Set<ResponseType>([
  ResponseType.CHAIN_SUB,
  ResponseType.BATTLE_START,
  ResponseType.BATTLE_LOG_EVENT,
  ResponseType.BATTLE_LOG_SNAPSHOT,
  ResponseType.SIGNED_COMMIT_RECEIVED,
  ResponseType.DUAL_SIGNED_REVEAL_RECEIVED,
  ResponseType.COMMITTER_REVEAL_RECEIVED,
  ResponseType.DUAL_SIGNED_SLOT_REVEAL_RECEIVED,
  ResponseType.COMMITTER_SLOT_REVEAL_RECEIVED,
  ResponseType.SLOT_CONTRIB_RECEIVED,
  ResponseType.COOP_TURN_RECEIVED,
  ResponseType.SIGNED_QUEUE_MATCHED,
  ResponseType.SIGNED_QUEUE_MATCH_ASSIGNED,
  ResponseType.QUEUE_MATCH_ABORTED,
  ResponseType.SIGNED_DIRECT_CHALLENGE_RECEIVED,
  ResponseType.PARTY_CARD,
  ResponseType.SEAT_CLAIM_RESULT,
  ResponseType.PARTY_UPDATED,
  ResponseType.PARTY_READY,
  ResponseType.QUEUE_JOIN_REJECTED,
  ResponseType.QUEUE_LISTINGS,
  ResponseType.CLIENT_JOINED_BATTLE,
  ResponseType.CLIENT_LEFT_BATTLE,
  ResponseType.STALE_TURN,
  ResponseType.BATTLE_PRESENCE,
]);

const OUTBOUND_WS_WHITELIST: ReadonlySet<RequestType> = new Set<RequestType>([
  RequestType.SIGNED_COMMIT,
  RequestType.DUAL_SIGNED_REVEAL,
  RequestType.COMMITTER_REVEAL,
  RequestType.DUAL_SIGNED_SLOT_REVEAL,
  RequestType.COMMITTER_SLOT_REVEAL,
  RequestType.SLOT_CONTRIB,
  RequestType.COOP_TURN,
  RequestType.JOIN_SIGNED_QUEUE,
  RequestType.LEAVE_SIGNED_QUEUE,
  RequestType.SIGNED_DIRECT_CHALLENGE,
  RequestType.SIGNED_BATTLE_INITIATE,
  RequestType.BATTLE_SUB,
  RequestType.BATTLE_UNSUB,
]);

function summarizeInbound(data: MyResponse): Record<string, unknown> | undefined {
  const p: any = (data as any).payload ?? {};
  switch (data.type) {
    case ResponseType.CHAIN_SUB:
      return { topic: prefixHex(p?.topics?.[0]) };
    case ResponseType.BATTLE_START:
      return { battleKey: p.battleKey, p0: p.p0, p1: p.p1 };
    case ResponseType.BATTLE_LOG_EVENT:
      return { eventType: p?.event?.type, battleKey: p?.event?.battleKey };
    case ResponseType.BATTLE_LOG_SNAPSHOT:
      return { count: Array.isArray(p.events) ? p.events.length : 0 };
    case ResponseType.SIGNED_COMMIT_RECEIVED:
      return { battleKey: p.battleKey, turnId: p.turnId, moveHash: prefixHex(p.moveHash) };
    case ResponseType.DUAL_SIGNED_REVEAL_RECEIVED:
      return {
        battleKey: p.battleKey,
        turnId: p.turnId,
        revealerMoveIndex: p.revealerMoveIndex,
        committerMoveHash: prefixHex(p.committerMoveHash),
      };
    case ResponseType.COMMITTER_REVEAL_RECEIVED:
      return { battleKey: p.battleKey, turnId: p.turnId, committerMoveIndex: p.committerMoveIndex };
    case ResponseType.DUAL_SIGNED_SLOT_REVEAL_RECEIVED:
      return {
        battleKey: p.battleKey,
        turnId: p.turnId,
        revealerSidePacked: prefixHex(p.revealerSidePacked),
        committerMovesHash: prefixHex(p.committerMovesHash),
      };
    case ResponseType.COMMITTER_SLOT_REVEAL_RECEIVED:
      return { battleKey: p.battleKey, turnId: p.turnId, committerSidePacked: prefixHex(p.committerSidePacked) };
    case ResponseType.SLOT_CONTRIB_RECEIVED:
      return { battleKey: p.battleKey, turnId: p.turnId, seat: p.seat, moveIndex: p.moveIndex };
    case ResponseType.SIGNED_QUEUE_MATCHED:
    case ResponseType.SIGNED_QUEUE_MATCH_ASSIGNED:
      return { battleKey: p.battleKey, p0: p.p0, p1: p.p1 };
    case ResponseType.QUEUE_MATCH_ABORTED:
      return { reason: p.reason };
    case ResponseType.SIGNED_DIRECT_CHALLENGE_RECEIVED:
      return { from: p.p0, battleKey: p.battleKey };
    case ResponseType.PARTY_CARD:
      return { partyId: p.partyId, host: p.host, yourSeatIndex: p.yourSeatIndex };
    case ResponseType.SEAT_CLAIM_RESULT:
      return { partyId: p.partyId, seatIndex: p.seatIndex, accepted: p.accepted, reason: p.reason };
    case ResponseType.PARTY_UPDATED:
      return { partyId: p.partyId, seats: Array.isArray(p.seats) ? p.seats.length : 0 };
    case ResponseType.PARTY_READY:
      return { partyId: p.partyId, isCpuInclusive: p.isCpuInclusive };
    case ResponseType.QUEUE_JOIN_REJECTED:
      return { reason: p.reason };
    case ResponseType.QUEUE_LISTINGS:
      return { count: Array.isArray(p.listings) ? p.listings.length : 0, total: p.total };
    case ResponseType.CLIENT_JOINED_BATTLE:
    case ResponseType.CLIENT_LEFT_BATTLE:
      return { battleKey: p.battleKey, address: p.address };
    case ResponseType.STALE_TURN:
      return { battleKey: p.battleKey, currentTurnId: p.currentTurnId };
    case ResponseType.BATTLE_PRESENCE:
      return { battleKey: p.battleKey, address: p.address, status: p.status };
  }
  return undefined;
}

function summarizeOutbound(req: Request): Record<string, unknown> | undefined {
  const p: any = (req as any).payload ?? {};
  switch (req.type) {
    case RequestType.SIGNED_COMMIT:
      return { battleKey: p.battleKey, turnId: p.turnId, moveHash: prefixHex(p.moveHash) };
    case RequestType.DUAL_SIGNED_REVEAL:
      return {
        battleKey: p.battleKey,
        turnId: p.turnId,
        revealerMoveIndex: p.revealerMoveIndex,
        committerMoveHash: prefixHex(p.committerMoveHash),
      };
    case RequestType.COMMITTER_REVEAL:
      return { battleKey: p.battleKey, turnId: p.turnId, committerMoveIndex: p.committerMoveIndex };
    case RequestType.JOIN_SIGNED_QUEUE:
      return { hasOffer: !!p.offer, hasSignature: !!p.signature };
    case RequestType.SIGNED_DIRECT_CHALLENGE:
      return { target: p?.offer?.p1, hasSignature: !!p.signature };
    case RequestType.SIGNED_BATTLE_INITIATE:
      return { battleKey: p?.offer?.battleKey };
    case RequestType.BATTLE_SUB:
    case RequestType.BATTLE_UNSUB:
      return { battleKey: p.battleKey };
    case RequestType.LEAVE_SIGNED_QUEUE:
      return {};
  }
  return undefined;
}

@Injectable({
  providedIn: 'root',
})
export class ServerService {
  private websocket: string = environment.wsEndpoint;
  private socket!: WebSocket;
  private requestQueue: Request[] = [];

  private reconnectAttempts = 0;
  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private hasConnectedOnce = false;
  private readonly INITIAL_RECONNECT_DELAY = 1000;
  private readonly MAX_RECONNECT_DELAY = 30000;
  private readonly RECONNECT_MULTIPLIER = 2;

  public socketConnectedEvent: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public isReconnecting = signal(false);
  // Presence enumeration only — payloads carry usernames inline. Keyed lowercase
  // because the server mixes both castings on the wire; read via clientFor().
  public clients = signal(
    new Map<Lowercase<Hex>, OnlineUser>()
  );

  clientFor(addr: Hex | undefined): OnlineUser | undefined {
    if (!addr) return undefined;
    return this.clients().get(addr.toLowerCase() as Lowercase<Hex>);
  }

  twitterHandleFor(addr: Hex | undefined): string | null {
    return this.clientFor(addr)?.twitterHandle ?? null;
  }
  public chainSubscriptionEvent: Subject<{
    payload: ChainSubscriptionResponsePayload;
    source: 'chain' | 'server';
  }> = new Subject<{
    payload: ChainSubscriptionResponsePayload;
    source: 'chain' | 'server';
  }>();
  public chatMessage: Subject<ChatMessageResponsePayload> =
    new Subject<ChatMessageResponsePayload>();
  public battleStart: Subject<BattleStartResponsePayload> =
    new Subject<BattleStartResponsePayload>();

  public battleNotifications: Subject<{
    battleKey: Hex;
    message: string;
  }> = new Subject<{
    battleKey: Hex;
    message: string;
  }>();

  // Battle-log feed — snapshot sent on auth, single-event broadcasts thereafter.
  public battleLogSnapshot: ReplaySubject<BattleLogEvent[]> = new ReplaySubject<BattleLogEvent[]>(1);
  public battleLogEvent: Subject<BattleLogEvent> = new Subject<BattleLogEvent>();

  public signedQueueMatched: Subject<SignedQueueMatchedPayload> = new Subject<SignedQueueMatchedPayload>();
  public signedQueueMatchAssigned: Subject<SignedQueueMatchAssignedPayload> = new Subject<SignedQueueMatchAssignedPayload>();
  public queueMatchAborted: Subject<QueueMatchAbortedPayload> = new Subject<QueueMatchAbortedPayload>();
  public queueState: Subject<QueueStatePayload> = new Subject<QueueStatePayload>();
  // Open-queue adverts, replaced wholesale per push. A signal, not a Subject —
  // a view mounting mid-session has to read the current set, not wait for a push.
  public queueListings = signal<readonly QueueListing[]>([]);
  public queueListingsTotal = signal(0);
  public signedDirectChallengeReceived: Subject<SignedDirectChallengeReceivedPayload> = new Subject<SignedDirectChallengeReceivedPayload>();

  public partyCard$: Subject<PartyCardPayload> = new Subject<PartyCardPayload>();
  public seatClaimResult$: Subject<SeatClaimResultPayload> = new Subject<SeatClaimResultPayload>();
  public partyUpdated$: Subject<PartyUpdatedPayload> = new Subject<PartyUpdatedPayload>();
  public partyReady$: Subject<PartyReadyPayload> = new Subject<PartyReadyPayload>();

  public frensTicket$: BehaviorSubject<FrensTicketPayload | null> =
    new BehaviorSubject<FrensTicketPayload | null>(null);

  public signedCommitReceived: Subject<SignedCommitPayload> = new Subject<SignedCommitPayload>();
  public dualSignedRevealReceived: Subject<DualSignedRevealPayload> = new Subject<DualSignedRevealPayload>();
  public committerRevealReceived: Subject<CommitterRevealPayload> = new Subject<CommitterRevealPayload>();
  public dualSignedSlotRevealReceived: Subject<DualSignedSlotRevealPayload> = new Subject<DualSignedSlotRevealPayload>();
  public committerSlotRevealReceived: Subject<CommitterSlotRevealPayload> = new Subject<CommitterSlotRevealPayload>();
  public slotContribReceived: Subject<SlotContribPayload> = new Subject<SlotContribPayload>();
  public coopTurnReceived: Subject<CoopTurnPayload> = new Subject<CoopTurnPayload>();
  public staleTurn: Subject<StaleTurnPayload> = new Subject<StaleTurnPayload>();
  public battlePresence: Subject<BattlePresencePayload> = new Subject<BattlePresencePayload>();
  public simplePMSharesBought: Subject<SimplePMSharesBoughtPayload> = new Subject<SimplePMSharesBoughtPayload>();
  public simplePMSharesClaimed: Subject<SimplePMSharesClaimedPayload> = new Subject<SimplePMSharesClaimedPayload>();
  public battleSettled: Subject<BattleSettledPayload> = new Subject<BattleSettledPayload>();

  public queueJoinRejected: Subject<QueueJoinRejectedPayload> = new Subject<QueueJoinRejectedPayload>();
  public leaderboard: Subject<LeaderboardResponsePayload> = new Subject<LeaderboardResponsePayload>();
  public dailyBoard: Subject<DailyBoardPayload> = new Subject<DailyBoardPayload>();
  public profile: Subject<ProfileResponsePayload> = new Subject<ProfileResponsePayload>();
  /** Latest server snapshot — BehaviorSubject so a late subscriber on /stats
   *  gets the cached payload immediately if a prior request landed. */
  public gameStats: BehaviorSubject<GameStatsPayload | null> = new BehaviorSubject<GameStatsPayload | null>(null);
  public twitterLinkBeginReply: Subject<LinkTwitterBeginReplyPayload> = new Subject<LinkTwitterBeginReplyPayload>();
  public twitterLinked: Subject<TwitterLinkedPayload> = new Subject<TwitterLinkedPayload>();
  public twitterUnlinked: Subject<TwitterUnlinkedPayload> = new Subject<TwitterUnlinkedPayload>();
  public twitterLinkRejected: Subject<LinkTwitterRejectedPayload> = new Subject<LinkTwitterRejectedPayload>();

  // Fires on reconnect (not initial connect) so consumers can re-auth/re-subscribe
  public reconnected: Subject<void> = new Subject<void>();

  // Messages that only forward their payload; each entry type-checks against the wire union.
  private readonly forwardRoutes: ForwardRoutes = {
    [ResponseType.CHAT_MSG]: this.chatMessage,
    [ResponseType.BATTLE_START]: this.battleStart,
    [ResponseType.SIGNED_QUEUE_MATCHED]: this.signedQueueMatched,
    [ResponseType.SIGNED_QUEUE_MATCH_ASSIGNED]: this.signedQueueMatchAssigned,
    [ResponseType.QUEUE_MATCH_ABORTED]: this.queueMatchAborted,
    [ResponseType.QUEUE_STATE]: this.queueState,
    [ResponseType.SIGNED_DIRECT_CHALLENGE_RECEIVED]: this.signedDirectChallengeReceived,
    [ResponseType.PARTY_CARD]: this.partyCard$,
    [ResponseType.SEAT_CLAIM_RESULT]: this.seatClaimResult$,
    [ResponseType.PARTY_UPDATED]: this.partyUpdated$,
    [ResponseType.PARTY_READY]: this.partyReady$,
    [ResponseType.FRENS_TICKET]: this.frensTicket$,
    [ResponseType.SIGNED_COMMIT_RECEIVED]: this.signedCommitReceived,
    [ResponseType.DUAL_SIGNED_REVEAL_RECEIVED]: this.dualSignedRevealReceived,
    [ResponseType.COMMITTER_REVEAL_RECEIVED]: this.committerRevealReceived,
    [ResponseType.DUAL_SIGNED_SLOT_REVEAL_RECEIVED]: this.dualSignedSlotRevealReceived,
    [ResponseType.COMMITTER_SLOT_REVEAL_RECEIVED]: this.committerSlotRevealReceived,
    [ResponseType.SLOT_CONTRIB_RECEIVED]: this.slotContribReceived,
    [ResponseType.COOP_TURN_RECEIVED]: this.coopTurnReceived,
    [ResponseType.STALE_TURN]: this.staleTurn,
    [ResponseType.BATTLE_PRESENCE]: this.battlePresence,
    [ResponseType.SIMPLE_PM_SHARES_BOUGHT]: this.simplePMSharesBought,
    [ResponseType.SIMPLE_PM_SHARES_CLAIMED]: this.simplePMSharesClaimed,
    [ResponseType.BATTLE_SETTLED]: this.battleSettled,
    [ResponseType.QUEUE_JOIN_REJECTED]: this.queueJoinRejected,
    [ResponseType.DAILY_BOARD]: this.dailyBoard,
    [ResponseType.LEADERBOARD]: this.leaderboard,
    [ResponseType.PROFILE]: this.profile,
    [ResponseType.GAME_STATS]: this.gameStats,
    [ResponseType.LINK_TWITTER_BEGIN_REPLY]: this.twitterLinkBeginReply,
    [ResponseType.LINK_TWITTER_REJECTED]: this.twitterLinkRejected,
  };

  private wsHistory: WsLogEntry[] = [];

  // Heartbeat: any inbound message bumps this; the watchdog closes the
  // socket if it lags too far behind, forcing the reconnect path.
  private lastInboundTs = 0;
  private watchdogId: ReturnType<typeof setInterval> | null = null;
  private readonly WATCHDOG_POLL_MS = 2_000;
  private readonly WATCHDOG_STALE_MS = 15_000;
  // In-battle: belch pings every 3s, so 8s = two missed pings + jitter; a dead
  // socket mid-turn stalls the opponent, so detect and reconnect fast.
  private readonly WATCHDOG_BATTLE_STALE_MS = 8_000;
  private readonly BATTLE_RECONNECT_DELAY_CAP_MS = 2_000;

  // Set by BattleLoader while a full (non-chat-only) battle subscription is live.
  public battleKeepalive = signal(false);

  constructor() {
    this.connect();
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.lastInboundTs = Date.now();
      }
    });
  }

  public getWsHistory(): readonly WsLogEntry[] {
    return this.wsHistory;
  }

  private recordWs(entry: WsLogEntry): void {
    this.wsHistory.push(entry);
    if (this.wsHistory.length > WS_HISTORY_LIMIT) {
      this.wsHistory.splice(0, this.wsHistory.length - WS_HISTORY_LIMIT);
    }
  }

  private connect() {
    this.socket = new WebSocket(this.websocket);

    this.socket.addEventListener('open', () => {
      const isReconnect = this.hasConnectedOnce;
      this.reconnectAttempts = 0;
      this.isReconnecting.set(false);
      this.hasConnectedOnce = true;
      this.socketConnectedEvent.next(true);
      this.lastInboundTs = Date.now();
      this.startWatchdog();

      this.requestQueue.forEach((message) => {
        this.socket.send(JSON.stringify(message, bigintReplacer));
      });
      this.requestQueue = [];

      if (isReconnect) {
        this.reconnected.next();
      }
    });

    this.socket.addEventListener('close', () => {
      this.socketConnectedEvent.next(false);
      this.stopWatchdog();
      this.scheduleReconnect();
    });

    this.socket.addEventListener('error', () => {
      this.socketConnectedEvent.next(false);
      this.stopWatchdog();
      // Close event will fire after error, which will trigger reconnect
    });

    this.socket.addEventListener('message', this.handleMessage.bind(this));
  }

  // Closes the socket so the existing reconnect path replays the open/auth
  // flow with a fresh ws. Used by the wallet on account switch to avoid
  // in-place re-auth — the server's disconnect handler resets identity-keyed
  // state (queue/presence) cleanly via the cold path.
  public forceReconnect(): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.close();
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimeoutId !== null) {
      return;
    }

    this.isReconnecting.set(true);

    // Calculate delay with exponential backoff (capped low mid-battle — every
    // second of backoff is a second the opponent watches a frozen turn)
    const maxDelay = this.battleKeepalive() ? this.BATTLE_RECONNECT_DELAY_CAP_MS : this.MAX_RECONNECT_DELAY;
    const delay = Math.min(
      this.INITIAL_RECONNECT_DELAY * Math.pow(this.RECONNECT_MULTIPLIER, this.reconnectAttempts),
      maxDelay
    );

    console.log(`WebSocket disconnected. Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1})...`);

    this.reconnectTimeoutId = setTimeout(() => {
      this.reconnectTimeoutId = null;
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }

  private startWatchdog() {
    this.stopWatchdog();
    this.watchdogId = setInterval(() => {
      const staleMs = this.battleKeepalive() ? this.WATCHDOG_BATTLE_STALE_MS : this.WATCHDOG_STALE_MS;
      if (Date.now() - this.lastInboundTs > staleMs) {
        console.log(`[ws] No inbound for ${staleMs}ms; forcing reconnect.`);
        this.socket.close();
      }
    }, this.WATCHDOG_POLL_MS);
  }

  private stopWatchdog() {
    if (this.watchdogId !== null) {
      clearInterval(this.watchdogId);
      this.watchdogId = null;
    }
  }

  public handleChainMessage(data: ChainSubscriptionResponsePayload) {
    this.chainSubscriptionEvent.next({ payload: data, source: 'chain' });
  }

  // Apply a single-client mutation to a fresh copy of the clients map and publish it.
  // `fn` receives the previous entry (or undefined) and returns the next entry, or
  // undefined to delete. Returns the previous entry for call-site side-effects.
  private updateClient(
    addr: Hex,
    fn: (prev: OnlineUser | undefined) => OnlineUser | undefined
  ): OnlineUser | undefined {
    const key = addr.toLowerCase() as Lowercase<Hex>;
    const map = new Map(this.clients());
    const prev = map.get(key);
    const next = fn(prev);
    if (next === undefined) {
      map.delete(key);
    } else {
      map.set(key, next);
    }
    this.clients.set(map);
    return prev;
  }

  // Patch an already-known client's twitterHandle; no-op (no emission) if we've
  // never seen the address.
  private setTwitterHandle(addr: Hex, twitterHandle: string | null): void {
    if (!this.clientFor(addr)) return;
    this.updateClient(addr, (prev) => ({ ...prev!, twitterHandle }));
  }

  // Merge a payload's username/twitterHandle with whatever we have cached for
  // the address in the live client list, preferring the payload's own values.
  // The shared first step behind chat rows and the recent-battles bar so both
  // resolve identity from the same source before formatting a display name.
  resolveIdentity<
    T extends { address?: Hex; username?: string | null; twitterHandle?: string | null }
  >(input: T): T & { username: string | null; twitterHandle: string | null } {
    const cached = this.clientFor(input.address);
    return {
      ...input,
      username: input.username || cached?.username || null,
      twitterHandle: input.twitterHandle || cached?.twitterHandle || null,
    };
  }

  private handleMessage(event: MessageEvent) {
    const data: MyResponse = JSON.parse(event.data);
    const now = Date.now();
    this.lastInboundTs = now;

    if (data.type === ResponseType.PING) {
      this.socket.send(JSON.stringify({ type: RequestType.PONG }));
      return;
    }

    if (INBOUND_WS_WHITELIST.has(data.type)) {
      this.recordWs({
        ts: now,
        direction: 'IN',
        type: data.type,
        summary: summarizeInbound(data),
      });
    }
    const forward = this.forwardRoutes[data.type] as { next(payload: unknown): void } | undefined;
    if (forward && 'payload' in data) {
      forward.next(data.payload);
      return;
    }

    switch (data.type) {
      case ResponseType.ALL_CLIENTS:
        this.clients.set(
          new Map(
            data.payload.clients.map((client) => [
              client.address.toLowerCase() as Lowercase<Hex>,
              onlineUserFromPayload(client.username, client.stats, client.twitterHandle),
            ])
          )
        );
        break;
      case ResponseType.CHAIN_SUB:
        this.chainSubscriptionEvent.next({
          payload: data.payload,
          source: 'server',
        });
        break;
      case ResponseType.USERNAME_SET:
        // The broadcast carries only address + username; keep the entry's stats and handle.
        this.updateClient(data.payload.address, (prev) => ({ ...prev, username: data.payload.username }));
        break;
      case ResponseType.CHAT_HISTORY:
        data.payload.messages.forEach((message: ChatHistoryMessage) => {
          this.chatMessage.next({
            id: message.id,
            battleKey: data.payload.battleKey,
            message: message.message,
            address: message.address,
            username: message.username,
            twitterHandle: message.twitterHandle,
            timestamp: message.timestamp,
          });
        });
        break;
      case ResponseType.CLIENT_JOINED_BATTLE:
        this.battleNotifications.next({
          battleKey: data.payload.battleKey,
          message: `${formatPlayerName(this.resolveIdentity(data.payload))} joined the battle`,
        });
        break;
      case ResponseType.CLIENT_LEFT_BATTLE:
        this.battleNotifications.next({
          battleKey: data.payload.battleKey,
          message: `${formatPlayerName(this.resolveIdentity(data.payload))} left the battle`,
        });
        break;
      case ResponseType.CLIENT_DELTA:
        const addedClients = data.payload.added;
        const removedClients = data.payload.removed;
        const currentClients = new Map(this.clients());
        for (const client of addedClients) {
          currentClients.set(
            client.address.toLowerCase() as Lowercase<Hex>,
            onlineUserFromPayload(client.username, client.stats, client.twitterHandle)
          );
        }
        for (const client of removedClients) {
          currentClients.delete(
            client.address.toLowerCase() as Lowercase<Hex>
          );
        }
        this.clients.set(currentClients);
        break;
      case ResponseType.BATTLE_LOG_SNAPSHOT:
        this.battleLogSnapshot.next(data.payload.events);
        break;
      case ResponseType.BATTLE_LOG_EVENT:
        this.battleLogEvent.next(data.payload.event);
        if (data.payload.event.type === 'BATTLE_START') {
          const ev = data.payload.event;
          this.battleStart.next({
            battleKey: ev.battleKey,
            p0: ev.p0,
            p0Username: ev.p0Username,
            p0Stats: ev.p0Stats,
            p0TwitterHandle: ev.p0TwitterHandle,
            p1: ev.p1,
            p1Username: ev.p1Username,
            p1Stats: ev.p1Stats,
            p1TwitterHandle: ev.p1TwitterHandle,
            p2: ev.p2,
            p3: ev.p3,
          });
        }
        break;
      case ResponseType.QUEUE_LISTINGS:
        this.queueListings.set(data.payload.listings);
        this.queueListingsTotal.set(data.payload.total);
        break;
      case ResponseType.TWITTER_LINKED:
        this.twitterLinked.next(data.payload);
        // Update our local view immediately so ALL_CLIENTS doesn't feel stale.
        this.setTwitterHandle(data.payload.address, data.payload.twitterHandle);
        break;
      case ResponseType.TWITTER_UNLINKED:
        this.twitterUnlinked.next(data.payload);
        this.setTwitterHandle(data.payload.address, null);
        break;
    }
  }

  isOnline(address: Hex | undefined): boolean {
    return this.clientFor(address) !== undefined;
  }

  queueMessage(message: Request) {
    const queued = !this.socketConnectedEvent.getValue();
    if (queued) {
      this.requestQueue.push(message);
    } else {
      this.socket.send(JSON.stringify(message, bigintReplacer));
    }
    if (OUTBOUND_WS_WHITELIST.has(message.type)) {
      const summary = summarizeOutbound(message);
      this.recordWs({
        ts: Date.now(),
        direction: 'OUT',
        type: message.type,
        summary: queued ? { ...(summary ?? {}), queued: true } : summary,
      });
    }
  }
}
